import { apiRequest } from './client';

/**
 * X3.6 — the Integrations settings screen's client (D0 dual-path).
 *
 * Every read shape here mirrors the server's `registryView()` and the
 * `connector_configs` row. Secrets are **never** returned: `configured` is the
 * only signal that a BYO key is stored (the server checks `connector_secrets`
 * and active subscriptions).
 */

export type LicenceClass = 'commercial_ok' | 'byo_key_only' | 'platform_brokered';

export type Attribution = { required: boolean; text: string; url?: string };

export type BrokeredPlan = {
  price_label: string;
  terms_url: string;
  retention_days: number;
  metered_per: 'screen';
};

export type ConnectorEntry = {
  id: string;
  data_type: string;
  provider_name: string;
  licence_class: LicenceClass;
  attribution: Attribution;
  byo_supported: boolean;
  brokered: boolean;
  /** Present only when `brokered`; shown BEFORE consent (D0). */
  brokered_plan: BrokeredPlan | null;
  refresh_interval_s: number;
  enabled: boolean;
};

export type ConnectorConfigRow = {
  connector_id: string;
  path: 'byo' | 'brokered';
  params: Record<string, unknown>;
  enabled: boolean;
  /** True when a BYO secret or an active brokered subscription exists. */
  configured: boolean;
};

export type ConnectorExport = {
  version: number;
  connectors: Array<{ connector_id: string; path: string; params: Record<string, unknown>; enabled: boolean }>;
  subscriptions: Array<{ connector_id: string; metered_per: string }>;
};

export type Metering = { connector_id: string; metered_per: string; screen_count: number; billable_units: number };

/** The provider registry (one entry per provider). Admin-authenticated. */
export async function listConnectors(): Promise<ConnectorEntry[]> {
  const body = await apiRequest<{ connectors: ConnectorEntry[] }>('/connectors');
  return body.connectors ?? [];
}

/** This tenant's connector configs (never includes secrets). */
export async function listConnectorConfigs(): Promise<ConnectorConfigRow[]> {
  const body = await apiRequest<{ configs: ConnectorConfigRow[] }>('/connectors/config');
  return body.configs ?? [];
}

/**
 * Save a connector config. `secret` is write-once: passing it stores/replaces
 * the key server-side and it is never read back. Omit it to change params/path
 * without touching the stored key.
 */
export async function saveConnectorConfig(input: {
  connector_id: string;
  path: 'byo' | 'brokered';
  params?: Record<string, unknown>;
  secret?: string;
}): Promise<{ ok: boolean; connector_id: string; path: string }> {
  return apiRequest('/connectors/config', { method: 'POST', body: input });
}

/** Remove the tenant's config *and* stored secret for a connector. */
export async function removeConnectorConfig(connectorId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/connectors/config/${encodeURIComponent(connectorId)}`, { method: 'DELETE' });
}

/** Path 2 — opt in to the platform-brokered plan (metered per screen). */
export async function optIn(connectorId: string): Promise<{ ok: boolean; status: string }> {
  return apiRequest(`/connectors/${encodeURIComponent(connectorId)}/opt-in`, { method: 'POST' });
}

/** Back to Path 1: cancels the subscription; the tenant's own config is kept. */
export async function optOut(connectorId: string): Promise<{ ok: boolean; status: string }> {
  return apiRequest(`/connectors/${encodeURIComponent(connectorId)}/opt-out`, { method: 'POST' });
}

export async function fetchMetering(connectorId: string): Promise<Metering> {
  return apiRequest(`/connectors/${encodeURIComponent(connectorId)}/metering`);
}

/** Portability (D0): every config minus secrets, for a tenant leaving. */
export async function exportConnectors(): Promise<ConnectorExport> {
  return apiRequest('/connectors/export');
}
